import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { useAuth } from "@clerk/react";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { isAuthDisabled } from "@/lib/authMode";
import {
  ArrowRight,
  Mail,
  Sparkles,
  CheckCircle2,
  Bot,
  FolderKanban,
  Calendar,
  Lock,
  Users,
  Zap,
  Shield,
  Eye,
  Download,
  Check,
  Inbox,
  ChevronRight,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LegalFooter from "@/components/legal-footer";
import LanguageSwitcher from "@/components/language-switcher";
import Logo from "@/components/logo";

// ─── Utils ─────────────────────────────────────────────────────────────

function useInViewRef(margin = "-60px") {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin });
  return { ref, inView };
}

// ─── Top-level page ────────────────────────────────────────────────────

export default function WaitlistPage() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  return (
    <div className="min-h-[100dvh] bg-background text-foreground antialiased overflow-x-hidden">
      <WaitlistNav />
      <WaitlistHero />
      <StatsBand />
      <BeforeAfterSection />
      <FeaturesSection />
      <ForWhoSection />
      <SecuritySection />
      <FinalCTA />
      <LegalFooter />
    </div>
  );
}

// ─── Nav ───────────────────────────────────────────────────────────────

function WaitlistNav() {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const bg = useTransform(scrollY, [0, 80], ["hsl(var(--background) / 0)", "hsl(var(--background) / 0.92)"]);
  const border = useTransform(scrollY, [0, 80], ["hsl(var(--border) / 0)", "hsl(var(--border) / 1)"]);
  const { isSignedIn: clerkSignedIn } = useAuth();
  const loggedIn = isAuthDisabled() || !!clerkSignedIn;

  return (
    <motion.header
      style={{ backgroundColor: bg, borderBottomColor: border }}
      className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-md"
    >
      <div className="mx-auto max-w-6xl flex items-center justify-between px-6 h-16">
        <Link href="/" className="flex items-center gap-2 group">
          <Logo size={32} />
          <span className="font-semibold tracking-tight text-lg">OneNext</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {loggedIn ? (
            <Link href="/app/today">
              <Button size="sm" className="gap-1.5">
                {t("nav.dashboard")}
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          ) : (
            <Link href="/sign-in">
              <Button size="sm" variant="outline" className="gap-1.5">
                {t("nav.login")}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </motion.header>
  );
}

// ─── Email form shared component ───────────────────────────────────────

function WaitlistForm({ dark = false }: { dark?: boolean }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        const d = await res.json().catch(() => ({}));
        setErrorMsg(d?.error ?? t("waitlist.form_error_default"));
        setStatus("error");
      }
    } catch {
      setErrorMsg(t("waitlist.form_error_network"));
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`flex items-center gap-3 rounded-xl border px-5 py-4 ${
          dark
            ? "bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground"
            : "bg-primary/5 border-primary/20 text-foreground"
        }`}
      >
        <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
        <div>
          <p className="text-sm font-semibold">{t("waitlist.form_success_title")}</p>
          <p className={`text-xs mt-0.5 ${dark ? "opacity-70" : "text-muted-foreground"}`}>
            {t("waitlist.form_success_desc")}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className={`flex flex-col sm:flex-row gap-2 ${dark ? "" : ""}`}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus("idle"); }}
          placeholder={t("waitlist.form_placeholder")}
          className={`flex-1 rounded-xl border px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
            dark
              ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40"
              : "bg-background border-border text-foreground placeholder:text-muted-foreground"
          }`}
        />
        <Button
          type="submit"
          size="lg"
          disabled={status === "loading"}
          className={`shrink-0 h-11 px-6 gap-2 ${
            dark
              ? "bg-white text-primary hover:bg-white/90"
              : "shadow-lg shadow-primary/20"
          }`}
        >
          {status === "loading" ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              {t("waitlist.form_cta")}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
      {status === "error" && (
        <p className={`mt-2 text-xs ${dark ? "text-red-300" : "text-destructive"}`}>
          {errorMsg}
        </p>
      )}
    </form>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────

function WaitlistHero() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden pt-32 pb-20 px-6">
      {/* Animated background */}
      <div aria-hidden className="absolute inset-0 -z-10 overflow-hidden">
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-primary/15 blur-3xl"
        />
        <motion.div
          animate={{ x: [0, -40, 0], y: [0, 50, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-10 -right-40 w-[560px] h-[560px] rounded-full bg-blue-500/10 blur-3xl"
        />
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[200px] bg-primary/5 blur-3xl"
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,hsl(var(--primary)/0.08),transparent_70%)]" />
      </div>

      <div className="mx-auto max-w-4xl text-center">
        {/* Early access badge */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/8 px-4 py-1.5 text-xs font-semibold text-primary mb-6"
        >
          <motion.span
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-primary"
          />
          {t("waitlist.badge")}
        </motion.div>

        {/* Main headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-4"
        >
          {t("waitlist.hero_title_1")}{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-primary">{t("waitlist.hero_title_2")}</span>
            <motion.span
              aria-hidden
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.7, ease: "easeOut" }}
              style={{ originX: 0 }}
              className="absolute left-0 bottom-1 h-3 w-full rounded-md bg-primary/20"
            />
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-xl md:text-2xl font-semibold text-muted-foreground mb-4"
        >
          {t("waitlist.hero_subtitle")}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10"
        >
          {t("waitlist.hero_body")}
        </motion.p>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-lg mx-auto mb-6"
        >
          <WaitlistForm />
        </motion.div>

        {/* Trust signals */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground"
        >
          {[
            t("waitlist.trust_1"),
            t("waitlist.trust_2"),
            t("waitlist.trust_3"),
          ].map((trust) => (
            <span key={trust} className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
              {trust}
            </span>
          ))}
        </motion.div>

        {/* Animated hero mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          className="relative mt-16 mx-auto max-w-3xl"
        >
          <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-[3rem] -z-10" />
          <HeroMockup />
        </motion.div>
      </div>
    </section>
  );
}

// Animated hero mockup showing inbox → today transformation
function HeroMockup() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"inbox" | "sorting" | "today">("inbox");

  useEffect(() => {
    const seq: Array<["inbox" | "sorting" | "today", number]> = [
      ["inbox", 2000],
      ["sorting", 1800],
      ["today", 2800],
    ];
    let i = 0;
    let timeout: ReturnType<typeof setTimeout>;
    function tick() {
      const [next, delay] = seq[i % seq.length];
      setPhase(next);
      timeout = setTimeout(tick, delay);
      i++;
    }
    tick();
    return () => clearTimeout(timeout);
  }, []);

  const emails = [
    { from: "Marc D.", subject: t("waitlist.mock_email1"), tag: "action", color: "text-primary bg-primary/10" },
    { from: "Newsletter", subject: t("waitlist.mock_email2"), tag: "noise", color: "text-muted-foreground bg-muted" },
    { from: "Acme Corp", subject: t("waitlist.mock_email3"), tag: "action", color: "text-primary bg-primary/10" },
    { from: "Sophie M.", subject: t("waitlist.mock_email4"), tag: "info", color: "text-amber-600 bg-amber-500/10" },
    { from: "LinkedIn", subject: t("waitlist.mock_email5"), tag: "noise", color: "text-muted-foreground bg-muted" },
  ];

  const tasks = [
    { title: t("waitlist.mock_task1"), time: "10 min", done: true },
    { title: t("waitlist.mock_task2"), time: "30 min", done: false },
    { title: t("waitlist.mock_task3"), time: "20 min", done: false },
  ];

  return (
    <div className="rounded-2xl border bg-card shadow-2xl overflow-hidden">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 px-4 py-3 border-b bg-muted/30">
        <span className="w-2.5 h-2.5 rounded-full bg-destructive/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-primary/60" />
        <div className="ml-3 flex items-center gap-2 text-xs text-muted-foreground">
          <AnimatePresence mode="wait">
            {phase === "inbox" && (
              <motion.span key="inbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Inbox className="w-3 h-3 inline mr-1" />
                {t("waitlist.mock_inbox_tab")} · 47 non lus
              </motion.span>
            )}
            {phase === "sorting" && (
              <motion.span key="sort" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Sparkles className="w-3 h-3 inline mr-1 text-primary" />
                {t("waitlist.mock_sorting_tab")}
              </motion.span>
            )}
            {phase === "today" && (
              <motion.span key="today" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <CheckCircle2 className="w-3 h-3 inline mr-1 text-primary" />
                {t("waitlist.mock_today_tab")}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="p-5 min-h-[220px] relative">
        <AnimatePresence mode="wait">
          {/* Inbox view — chaos */}
          {phase === "inbox" && (
            <motion.div
              key="inbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-1.5"
            >
              {emails.map((email, i) => (
                <motion.div
                  key={email.subject}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.07 }}
                  className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span className="text-xs font-medium w-20 shrink-0 text-muted-foreground">{email.from}</span>
                  <span className="text-xs text-foreground truncate flex-1">{email.subject}</span>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-center text-xs text-muted-foreground pt-2"
              >
                + 42 {t("waitlist.mock_more_emails")}
              </motion.div>
            </motion.div>
          )}

          {/* Sorting phase */}
          {phase === "sorting" && (
            <motion.div
              key="sorting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-1.5"
            >
              {emails.map((email, i) => (
                <motion.div
                  key={email.subject}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5"
                >
                  <span className="text-xs font-medium w-20 shrink-0 text-muted-foreground">{email.from}</span>
                  <span className="text-xs text-foreground truncate flex-1">{email.subject}</span>
                  <motion.span
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.15 }}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${email.color}`}
                  >
                    {email.tag === "action" ? t("waitlist.tag_action") : email.tag === "info" ? t("waitlist.tag_info") : t("waitlist.tag_noise")}
                  </motion.span>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Today view — clarity */}
          {phase === "today" && (
            <motion.div
              key="today"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold">{t("waitlist.mock_today_title")}</span>
                <span className="text-xs text-muted-foreground">3 {t("waitlist.mock_tasks_label")}</span>
              </div>
              <div className="space-y-2">
                {tasks.map((task, i) => (
                  <motion.div
                    key={task.title}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-3 ${
                      task.done ? "bg-primary/5 border-primary/20" : "bg-background border-border"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-md border-2 flex items-center justify-center shrink-0 ${
                        task.done ? "bg-primary border-primary" : "border-border"
                      }`}
                    >
                      {task.done && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                    </div>
                    <span className={`flex-1 text-sm ${task.done ? "line-through text-muted-foreground" : "font-medium"}`}>
                      {task.title}
                    </span>
                    <span className="text-xs text-muted-foreground">{task.time}</span>
                  </motion.div>
                ))}
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-3 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2 flex items-start gap-2"
              >
                <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground leading-snug">{t("waitlist.mock_ai_tip")}</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Stats band ────────────────────────────────────────────────────────

function StatsBand() {
  const { t } = useTranslation();
  const { ref, inView } = useInViewRef();

  const stats = [
    { value: "2h", label: t("waitlist.stat1_label"), suffix: t("waitlist.stat1_suffix") },
    { value: "84%", label: t("waitlist.stat2_label"), suffix: "" },
    { value: "1", label: t("waitlist.stat3_label"), suffix: t("waitlist.stat3_suffix") },
    { value: "3", label: t("waitlist.stat4_label"), suffix: t("waitlist.stat4_suffix") },
  ];

  return (
    <section ref={ref} className="border-y bg-muted/20 py-12 px-6">
      <div className="mx-auto max-w-6xl grid grid-cols-2 lg:grid-cols-4 gap-8">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="text-center"
          >
            <div className="text-4xl font-bold tracking-tight text-foreground">
              {stat.value}
              {stat.suffix && (
                <span className="text-lg text-muted-foreground ml-1 font-normal">{stat.suffix}</span>
              )}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">{stat.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

// ─── Before / After ────────────────────────────────────────────────────

function BeforeAfterSection() {
  const { t } = useTranslation();
  const { ref, inView } = useInViewRef("-40px");
  const [active, setActive] = useState<"before" | "after">("before");

  // Auto-switch for effect
  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => {
      setActive((a) => (a === "before" ? "after" : "before"));
    }, 3000);
    return () => clearInterval(id);
  }, [inView]);

  const beforeItems = [
    t("waitlist.before1"),
    t("waitlist.before2"),
    t("waitlist.before3"),
    t("waitlist.before4"),
  ];
  const afterItems = [
    t("waitlist.after1"),
    t("waitlist.after2"),
    t("waitlist.after3"),
    t("waitlist.after4"),
  ];

  return (
    <section ref={ref} className="py-24 px-6 bg-gradient-to-b from-background via-primary/5 to-background">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">
            {t("waitlist.before_after_eyebrow")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            {t("waitlist.before_after_title")}
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            {t("waitlist.before_after_sub")}
          </p>
        </motion.div>

        {/* Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-1 rounded-full border bg-muted/40 p-1">
            {(["before", "after"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActive(tab)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  active === tab
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab === "before" ? t("waitlist.tab_before") : t("waitlist.tab_after")}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {active === "before" ? (
            <motion.div
              key="before"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border-2 border-destructive/20 bg-destructive/5 p-8"
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-full bg-destructive/15 flex items-center justify-center">
                  <Inbox className="w-4 h-4 text-destructive/70" />
                </div>
                <h3 className="font-semibold text-lg">{t("waitlist.tab_before")}</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {beforeItems.map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-3 rounded-xl bg-background/60 border border-destructive/15 px-4 py-3"
                  >
                    <span className="text-destructive/60 text-base mt-0.5">✗</span>
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="after"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border-2 border-primary/25 bg-primary/5 p-8"
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">{t("waitlist.tab_after")}</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {afterItems.map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-3 rounded-xl bg-background/80 border border-primary/15 px-4 py-3"
                  >
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

// ─── Features ──────────────────────────────────────────────────────────

interface WFeature {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  body: string;
  Visual: () => ReactNode;
  flip?: boolean;
}

function FeaturesSection() {
  const { t } = useTranslation();

  const features: WFeature[] = [
    {
      icon: <Mail className="w-5 h-5" />,
      eyebrow: t("waitlist.f1_eyebrow"),
      title: t("waitlist.f1_title"),
      body: t("waitlist.f1_body"),
      Visual: MailToActionVisual,
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      eyebrow: t("waitlist.f2_eyebrow"),
      title: t("waitlist.f2_title"),
      body: t("waitlist.f2_body"),
      Visual: TodayPrepVisual,
      flip: true,
    },
    {
      icon: <FolderKanban className="w-5 h-5" />,
      eyebrow: t("waitlist.f3_eyebrow"),
      title: t("waitlist.f3_title"),
      body: t("waitlist.f3_body"),
      Visual: ProjectContextVisual,
    },
    {
      icon: <Bot className="w-5 h-5" />,
      eyebrow: t("waitlist.f4_eyebrow"),
      title: t("waitlist.f4_title"),
      body: t("waitlist.f4_body"),
      Visual: AssistantPilotVisual,
      flip: true,
    },
  ];

  return (
    <section className="py-16 px-6">
      <div className="mx-auto max-w-6xl space-y-20">
        {features.map((f) => (
          <FeatureRow key={f.title} feature={f} />
        ))}
      </div>
    </section>
  );
}

function FeatureRow({ feature }: { feature: WFeature }) {
  const { ref, inView } = useInViewRef("-80px");
  const { Visual, flip } = feature;

  return (
    <div
      ref={ref}
      className={`grid lg:grid-cols-2 gap-12 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}
    >
      {/* Text side */}
      <motion.div
        initial={{ opacity: 0, x: flip ? 30 : -30 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.65, ease: "easeOut" }}
        className="space-y-4"
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold">
          {feature.icon}
          {feature.eyebrow}
        </div>
        <h3 className="text-3xl font-bold tracking-tight leading-snug">{feature.title}</h3>
        <p className="text-muted-foreground text-base leading-relaxed">{feature.body}</p>
      </motion.div>

      {/* Visual side */}
      <motion.div
        initial={{ opacity: 0, x: flip ? -30 : 30 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.65, delay: 0.1, ease: "easeOut" }}
        className="relative"
      >
        <div className="absolute -inset-4 bg-primary/8 blur-3xl rounded-3xl -z-10" />
        <div className="rounded-2xl border bg-card shadow-xl overflow-hidden h-64 relative">
          {inView && <Visual />}
        </div>
      </motion.div>
    </div>
  );
}

// ── Feature visuals ────────────────────────────────────────────────────

function MailToActionVisual() {
  const { t } = useTranslation();
  const emails = [
    { from: "Marc D.", tag: t("waitlist.tag_action"), color: "text-primary bg-primary/10 border-primary/20" },
    { from: "Newsletter", tag: t("waitlist.tag_noise"), color: "text-muted-foreground bg-muted border-border" },
    { from: "Acme Corp", tag: t("waitlist.tag_action"), color: "text-primary bg-primary/10 border-primary/20" },
    { from: "Sophie M.", tag: t("waitlist.tag_info"), color: "text-amber-600 bg-amber-500/10 border-amber-500/20" },
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s >= emails.length ? 0 : s + 1)), 700);
    return () => clearInterval(id);
  }, [emails.length]);

  return (
    <div className="absolute inset-0 p-5 flex flex-col justify-center gap-2">
      {emails.map((email, i) => (
        <motion.div
          key={email.from}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1, duration: 0.4 }}
          className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2.5"
        >
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground shrink-0">
            {email.from[0]}
          </div>
          <span className="flex-1 text-sm text-foreground truncate">{email.from}</span>
          {i < step && (
            <motion.span
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${email.color}`}
            >
              {email.tag}
            </motion.span>
          )}
        </motion.div>
      ))}
    </div>
  );
}

function TodayPrepVisual() {
  const { t } = useTranslation();
  const items = [
    t("waitlist.mock_task1"),
    t("waitlist.mock_task2"),
    t("waitlist.mock_task3"),
  ];
  const [done, setDone] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setDone((d) => (d >= items.length ? 0 : d + 1)), 1200);
    return () => clearInterval(id);
  }, [items.length]);

  return (
    <div className="absolute inset-0 p-5 flex flex-col justify-center">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
        {t("waitlist.mock_today_title")}
      </div>
      <div className="space-y-2">
        {items.map((item, i) => {
          const isDone = i < done;
          return (
            <motion.div
              key={item}
              layout
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                isDone ? "bg-primary/5 border-primary/20" : "bg-background border-border"
              }`}
            >
              <motion.div
                animate={{
                  backgroundColor: isDone ? "hsl(var(--primary))" : "hsl(var(--background))",
                  borderColor: isDone ? "hsl(var(--primary))" : "hsl(var(--border))",
                }}
                className="w-4 h-4 rounded-md border-2 flex items-center justify-center shrink-0"
              >
                {isDone && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
              </motion.div>
              <span className={`text-sm ${isDone ? "line-through text-muted-foreground" : "font-medium"}`}>
                {item}
              </span>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
        <motion.div
          animate={{ width: `${(done / items.length) * 100}%` }}
          transition={{ duration: 0.5 }}
          className="h-full bg-primary rounded-full"
        />
      </div>
    </div>
  );
}

function ProjectContextVisual() {
  const { t } = useTranslation();
  const nodes = [
    { id: "proj", label: t("waitlist.ctx_project"), x: 100, y: 32, icon: "📁", primary: true },
    { id: "mail", label: t("waitlist.ctx_mail"), x: 28, y: 90, icon: "✉️" },
    { id: "task", label: t("waitlist.ctx_task"), x: 172, y: 90, icon: "✓" },
    { id: "note", label: t("waitlist.ctx_note"), x: 28, y: 148, icon: "📝" },
    { id: "event", label: t("waitlist.ctx_event"), x: 172, y: 148, icon: "📅" },
  ];
  const edges = [
    ["proj", "mail"], ["proj", "task"], ["proj", "note"], ["proj", "event"],
  ];
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));

  return (
    <div className="absolute inset-0 flex items-center justify-center p-4">
      <svg viewBox="0 20 200 160" className="w-full h-full overflow-visible">
        {edges.map(([a, b], i) => {
          const na = nodeMap[a], nb = nodeMap[b];
          return (
            <motion.line
              key={`${a}-${b}`}
              x1={na.x} y1={na.y + 10} x2={nb.x} y2={nb.y}
              stroke="hsl(var(--primary) / 0.25)"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              initial={{ strokeDashoffset: 20 }}
              animate={{ strokeDashoffset: [20, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3, ease: "linear" }}
            />
          );
        })}
        {nodes.map((n, i) => (
          <g key={n.id}>
            <motion.rect
              x={n.x - 32} y={n.y - 14} width={64} height={26} rx={8}
              fill={n.primary ? "hsl(var(--primary))" : "hsl(var(--card))"}
              stroke={n.primary ? "hsl(var(--primary))" : "hsl(var(--border))"}
              strokeWidth={1}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.12, duration: 0.4 }}
            />
            <motion.text
              x={n.x} y={n.y + 4}
              textAnchor="middle"
              fontSize={9}
              fill={n.primary ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.12 + 0.2 }}
              style={{ fontFamily: "inherit" }}
            >
              {n.icon} {n.label}
            </motion.text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function AssistantPilotVisual() {
  const { t } = useTranslation();
  const messages = [
    { role: "user", text: t("waitlist.chat_q1") },
    { role: "ai", text: t("waitlist.chat_a1") },
    { role: "user", text: t("waitlist.chat_q2") },
    { role: "ai", text: t("waitlist.chat_a2") },
  ];
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    const id = setInterval(() => {
      setVisible((v) => (v >= messages.length ? 1 : v + 1));
    }, 1500);
    return () => clearInterval(id);
  }, [messages.length]);

  return (
    <div className="absolute inset-0 p-4 flex flex-col justify-end gap-1.5">
      {messages.slice(0, visible).map((msg, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
        >
          {msg.role === "ai" && (
            <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="w-2.5 h-2.5 text-primary" />
            </div>
          )}
          <div
            className={`max-w-[75%] rounded-xl px-3 py-1.5 text-[11px] leading-snug ${
              msg.role === "user"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-foreground"
            }`}
          >
            {msg.text}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// ─── For who ───────────────────────────────────────────────────────────

function ForWhoSection() {
  const { t } = useTranslation();
  const { ref, inView } = useInViewRef();

  const personas = [
    { icon: "🧑‍💼", title: t("waitlist.persona1_title"), desc: t("waitlist.persona1_desc") },
    { icon: "🚀", title: t("waitlist.persona2_title"), desc: t("waitlist.persona2_desc") },
    { icon: "🎨", title: t("waitlist.persona3_title"), desc: t("waitlist.persona3_desc") },
    { icon: "📊", title: t("waitlist.persona4_title"), desc: t("waitlist.persona4_desc") },
    { icon: "⚡", title: t("waitlist.persona5_title"), desc: t("waitlist.persona5_desc") },
  ];

  return (
    <section ref={ref} className="py-24 px-6 bg-gradient-to-b from-background via-primary/5 to-background">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">
            {t("waitlist.for_who_eyebrow")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t("waitlist.for_who_title")}</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">{t("waitlist.for_who_sub")}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {personas.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group rounded-2xl border bg-card p-6 hover:border-primary/30 hover:shadow-md transition-all"
            >
              <div className="text-3xl mb-3">{p.icon}</div>
              <h3 className="font-semibold mb-1">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
          {/* "Ceux qui n'ont pas besoin de s'organiser, mais d'avancer" */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: personas.length * 0.08 }}
            className="rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 p-6 flex items-center justify-center text-center sm:col-span-2 lg:col-span-1"
          >
            <div>
              <Star className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-sm font-medium text-primary">{t("waitlist.for_who_conclusion")}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Security ──────────────────────────────────────────────────────────

function SecuritySection() {
  const { t } = useTranslation();
  const { ref, inView } = useInViewRef();

  const items = [
    { icon: <Lock className="w-5 h-5" />, title: t("waitlist.sec1_title"), desc: t("waitlist.sec1_desc") },
    { icon: <Eye className="w-5 h-5" />, title: t("waitlist.sec2_title"), desc: t("waitlist.sec2_desc") },
    { icon: <Download className="w-5 h-5" />, title: t("waitlist.sec3_title"), desc: t("waitlist.sec3_desc") },
    { icon: <Shield className="w-5 h-5" />, title: t("waitlist.sec4_title"), desc: t("waitlist.sec4_desc") },
  ];

  return (
    <section ref={ref} className="py-24 px-6">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border bg-muted/20 p-10 md:p-14"
        >
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold mb-4">
              <Shield className="w-3.5 h-3.5" />
              {t("waitlist.sec_eyebrow")}
            </div>
            <h2 className="text-3xl font-bold tracking-tight">{t("waitlist.sec_title")}</h2>
            <p className="mt-3 text-muted-foreground max-w-lg mx-auto">{t("waitlist.sec_sub")}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {items.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: i % 2 === 0 ? -15 : 15 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                className="flex gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{item.title}</h4>
                  <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Final CTA ─────────────────────────────────────────────────────────

function FinalCTA() {
  const { t } = useTranslation();
  const { ref, inView } = useInViewRef();

  return (
    <section ref={ref} className="py-24 px-6">
      <div className="mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden bg-primary px-10 py-16 text-center text-primary-foreground"
        >
          {/* Background blobs */}
          <div aria-hidden className="absolute inset-0 overflow-hidden">
            <motion.div
              animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.15, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-2xl"
            />
            <motion.div
              animate={{ x: [0, -20, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-2xl"
            />
          </div>

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold mb-6">
              <Users className="w-3.5 h-3.5" />
              {t("waitlist.cta_badge")}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-snug mb-3">
              {t("waitlist.cta_title")}
            </h2>
            <p className="text-primary-foreground/80 max-w-lg mx-auto mb-8 leading-relaxed">
              {t("waitlist.cta_sub")}
            </p>
            <div className="max-w-md mx-auto mb-6">
              <WaitlistForm dark />
            </div>
            <p className="text-xs text-primary-foreground/60">{t("waitlist.cta_privacy")}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
